<template>
  <div v-if="schoolData" class="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6 text-gray-900 dark:text-[#F1F5F9] transition-colors">
    <div class="max-w-7xl mx-auto space-y-6 sm:space-y-8">
      <!-- ========================================= -->
      <!-- TOP HEADER & WEBSITE STATUS CARD          -->
      <!-- ========================================= -->
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <!-- Title & Breadcrumb -->
        <div>
          <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1.5">
            <School class="w-4 h-4" />
            <span>Institution Administration</span>
          </div>
          <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-[#F1F5F9] tracking-tight">
            School Details
          </h1>
          <p class="text-sm text-gray-500 dark:text-[#A6B4C8] mt-1">
            Institutional profile, administrative policies, campus location, and faculty schedules.
          </p>
        </div>

        <!-- Website Status Card (Admin Only - Visible when Website is Created) -->
        <div
          v-if="userRole === 'admin' && schoolData?.websiteEnabled"
          class="w-full lg:w-auto bg-white dark:bg-[#111C30] border border-gray-200/80 dark:border-[#263449] rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-sm transition-colors"
        >
          <div class="flex items-center justify-between sm:justify-start gap-4">
            <!-- Icon -->
            <div
              class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0"
            >
              <Globe class="w-6 h-6" />
            </div>

            <!-- Content -->
            <div class="min-w-0 pr-1">
              <div class="flex items-center gap-2">
                <span class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9]">Public Website</span>
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                  :class="
                    schoolData?.websiteStatus === 'disabled'
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900/50'
                      : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50'
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="schoolData?.websiteStatus === 'disabled' ? 'bg-rose-500' : 'bg-emerald-500 animate-pulse'"
                  ></span>
                  {{ schoolData?.websiteStatus === 'disabled' ? 'Offline' : 'Live' }}
                </span>
              </div>

              <p class="text-xs text-gray-500 dark:text-[#A6B4C8] truncate max-w-[210px] mt-0.5">
                {{ schoolData.customDomain || `${schoolData.slug || 'school'}.educanium.com` }}
              </p>
            </div>

            <!-- Action Button -->
            <div class="flex-shrink-0">
              <a
                :href="schoolData.customDomain ? `https://${schoolData.customDomain}` : `https://${schoolData.slug || 'school'}.educanium.com`"
                target="_blank"
                rel="noopener noreferrer"
                class="px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] text-gray-800 dark:text-[#F1F5F9] border border-gray-200 dark:border-[#263449] text-xs sm:text-sm font-semibold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <span>Visit Portal</span>
                <ExternalLink class="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================= -->
      <!-- HERO SCHOOL IDENTITY CARD                 -->
      <!-- ========================================= -->
      <div
        class="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white rounded-3xl p-6 sm:p-8 lg:p-9 shadow-xl border border-indigo-900/40 overflow-hidden"
      >
        <!-- Background Ambient Glow Accents -->
        <div class="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none"></div>
        <div class="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none"></div>

        <div class="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <!-- Left: Logo & Identity Details -->
          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <!-- Emblem Avatar -->
            <div
              class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-inner flex-shrink-0"
            >
              <School class="w-10 h-10 sm:w-12 sm:h-12 text-blue-200" />
            </div>

            <!-- Details -->
            <div>
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <ShieldCheck class="w-3.5 h-3.5" />
                  <span>Verified Campus</span>
                </span>
                <span v-if="schoolData.schoolCode" class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/10 text-blue-200 border border-white/10">
                  Code: {{ schoolData.schoolCode }}
                </span>
                <span v-if="schoolData.boardAffiliation || schoolData.board" class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-200 border border-indigo-400/20">
                  {{ schoolData.boardAffiliation || schoolData.board }}
                </span>
              </div>

              <h2 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-tight">
                {{ schoolData.schoolName }}
              </h2>

              <p v-if="schoolData.tagline" class="text-sm sm:text-base text-blue-100/90 italic mt-1 font-light">
                "{{ schoolData.tagline }}"
              </p>

              <div class="flex flex-wrap items-center gap-4 mt-3 text-xs sm:text-sm text-blue-200/90 font-medium">
                <div class="flex items-center gap-1.5">
                  <GraduationCap class="w-4 h-4 text-blue-300" />
                  <span class="capitalize">{{ schoolData.schoolType || 'General' }} School</span>
                </div>
                <div v-if="schoolData.establishmentYear" class="flex items-center gap-1.5">
                  <Calendar class="w-4 h-4 text-blue-300" />
                  <span>Est. {{ schoolData.establishmentYear }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <MapPin class="w-4 h-4 text-blue-300" />
                  <span>{{ schoolData.city }}, {{ schoolData.state }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Legacy Stat & Edit Button -->
          <div class="flex items-center gap-3 sm:gap-4 self-start lg:self-center">
            <!-- Legacy Badge -->
            <div
              v-if="schoolData.establishmentYear"
              class="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 text-center min-w-[96px]"
            >
              <div class="text-2xl font-black text-white leading-none">
                {{ getSchoolAge() }}
              </div>
              <div class="text-[11px] uppercase tracking-wider text-blue-200 font-semibold mt-1">
                Years Legacy
              </div>
            </div>

            <!-- Edit Details Action -->
            <router-link
              v-if="userRole === 'admin'"
              to="/editSchool"
              class="px-5 py-3 rounded-2xl bg-white hover:bg-blue-50 text-slate-900 font-bold text-sm shadow-md transition-all duration-200 hover:scale-[1.02] active:scale-95 flex items-center gap-2 cursor-pointer"
            >
              <Edit class="w-4 h-4 text-indigo-600" />
              <span>Edit Details</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- ========================================= -->
      <!-- MAIN TWO-COLUMN CONTENT GRID              -->
      <!-- ========================================= -->
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
        <!-- ======================================= -->
        <!-- LEFT COLUMN (2 COLS)                    -->
        <!-- ======================================= -->
        <div class="xl:col-span-2 space-y-6 sm:space-y-8">
          <!-- CARD 1: INSTITUTIONAL SPECIFICATIONS -->
          <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Info class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9]">
                    Institutional Profile
                  </h3>
                  <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8]">
                    Registered credentials, affiliations, and operational specifications.
                  </p>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <!-- School Name -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <School class="w-4 h-4 text-indigo-500" />
                  <span>Official Name</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9] break-words">
                  {{ schoolData.schoolName }}
                </p>
              </div>

              <!-- Category -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <GraduationCap class="w-4 h-4 text-blue-500" />
                  <span>Category</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9] capitalize">
                  {{ schoolData.schoolType || 'General' }} School
                </p>
              </div>

              <!-- Board / Affiliation -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <Award class="w-4 h-4 text-purple-500" />
                  <span>Board Affiliation</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9]">
                  {{ schoolData.boardAffiliation || schoolData.board || 'CBSE Affiliated' }}
                </p>
              </div>

              <!-- Established Year -->
              <div v-if="schoolData.establishmentYear" class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <Calendar class="w-4 h-4 text-emerald-500" />
                  <span>Established</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9]">
                  {{ schoolData.establishmentYear }}
                </p>
              </div>

              <!-- Legacy -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <Clock class="w-4 h-4 text-amber-500" />
                  <span>Legacy</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9]">
                  {{ getSchoolAge() }} Years Operational
                </p>
              </div>

              <!-- Office Hours -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 transition-colors">
                <div class="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] uppercase tracking-wider mb-1.5">
                  <Clock class="w-4 h-4 text-cyan-500" />
                  <span>Office Hours</span>
                </div>
                <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.officeHours || 'Mon–Sat · 8:00 AM–3:00 PM' }}
                </p>
              </div>
            </div>
          </div>

          <!-- CARD 2: LEAVE OVERVIEW / POLICY -->
          <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors">
            <!-- Header -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <CalendarDays class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9]">
                    {{ userRole === 'admin' ? 'School Leave Policy & Allocations' : 'My Leave Allocations' }}
                  </h3>
                  <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8]">
                    {{
                      userRole === 'admin'
                        ? 'Annual quota per category across all faculty and staff.'
                        : 'Your allocated and remaining leaves for the current term.'
                    }}
                  </p>
                </div>
              </div>

              <!-- Admin Action -->
              <button
                v-if="userRole === 'admin'"
                @click="openPolicyModal"
                class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl text-xs sm:text-sm font-semibold transition-colors shadow-sm self-start sm:self-auto cursor-pointer"
              >
                {{ leaveBalance && leaveBalance.balances?.length ? 'Configure Policy' : '+ Create Policy' }}
              </button>
            </div>

            <!-- Balances Grid -->
            <div
              v-if="leaveBalance && leaveBalance.balances?.length"
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div
                v-for="(item, index) in leaveBalance.balances"
                :key="index"
                class="relative rounded-2xl p-5 bg-gray-50/70 dark:bg-[#18243A] border border-gray-200/70 dark:border-[#263449] hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-all duration-200 group"
              >
                <!-- Top Row: Type and Badge / Menu -->
                <div class="flex justify-between items-start mb-4">
                  <div>
                    <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#A6B4C8]">
                      Leave Type
                    </span>
                    <h4 class="text-lg font-bold text-gray-900 dark:text-[#F1F5F9] mt-0.5">
                      {{ item.type }}
                    </h4>
                  </div>

                  <div class="flex items-center gap-2">
                    <!-- Remaining Badge -->
                    <div
                      class="px-3 py-1.5 rounded-xl border text-center"
                      :class="
                        item.remaining === 0
                          ? 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-400'
                          : 'bg-white dark:bg-[#111C30] border-gray-200 dark:border-[#263449] text-indigo-600 dark:text-indigo-400 shadow-xs'
                      "
                    >
                      <div class="text-base font-extrabold leading-tight">
                        {{ item.remaining }}
                      </div>
                      <div class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8]">
                        {{ userRole === 'admin' ? 'Days' : 'Left' }}
                      </div>
                    </div>

                    <!-- 3-Dot Menu (Admin) -->
                    <div v-if="userRole === 'admin'" class="relative">
                      <button
                        @click.stop="toggleMenu(index)"
                        class="p-2 rounded-xl text-gray-500 hover:text-gray-800 dark:text-[#A6B4C8] dark:hover:text-white hover:bg-gray-200/60 dark:hover:bg-[#1F2E4A] transition-colors cursor-pointer"
                        title="Options"
                      >
                        <MoreVertical class="w-4 h-4" />
                      </button>

                      <!-- Dropdown -->
                      <div
                        v-if="activeMenu === index"
                        class="absolute right-0 mt-2 w-36 bg-white dark:bg-[#111C30] border border-gray-200 dark:border-[#263449] rounded-xl shadow-xl z-50 overflow-hidden py-1"
                      >
                        <button
                          @click="openEditModal(item); closeMenu();"
                          class="w-full flex items-center gap-2 text-left px-3.5 py-2 text-xs font-semibold hover:bg-indigo-50 dark:hover:bg-[#18243A] text-indigo-600 dark:text-indigo-400 transition-colors cursor-pointer"
                        >
                          <Edit3 class="w-3.5 h-3.5" />
                          <span>Update</span>
                        </button>
                        <button
                          @click="requestDeleteLeaveType(item); closeMenu();"
                          class="w-full flex items-center gap-2 text-left px-3.5 py-2 text-xs font-semibold hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 transition-colors cursor-pointer"
                        >
                          <Trash2 class="w-3.5 h-3.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Usage Stats (Non-Admin View) -->
                <div v-if="userRole !== 'admin'" class="space-y-1.5 mt-2">
                  <div class="flex justify-between text-xs font-medium text-gray-600 dark:text-[#A6B4C8]">
                    <span>Used: {{ item.used }}</span>
                    <span>Total: {{ item.total }}</span>
                  </div>
                  <div class="w-full h-2 bg-gray-200 dark:bg-[#172235] rounded-full overflow-hidden">
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :class="item.remaining === 0 ? 'bg-rose-500' : 'bg-gradient-to-r from-indigo-500 to-blue-500'"
                      :style="{ width: item.total > 0 ? (item.used / item.total) * 100 + '%' : '0%' }"
                    ></div>
                  </div>
                </div>

                <!-- Total Display for Admin -->
                <div v-else class="text-xs font-semibold text-gray-500 dark:text-[#A6B4C8] pt-1">
                  Annual Allocation: <span class="text-gray-900 dark:text-[#F1F5F9] font-bold">{{ item.total }} Days</span>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-10 px-4">
              <div class="w-14 h-14 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-900/40 rounded-2xl flex items-center justify-center mx-auto mb-3 text-indigo-600 dark:text-indigo-400">
                <CalendarDays class="w-7 h-7" />
              </div>
              <h4 class="text-base font-bold text-gray-900 dark:text-[#F1F5F9]">
                {{ userRole === 'admin' ? 'No Leave Policy Configured' : 'Leave Policy Not Available' }}
              </h4>
              <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8] max-w-md mx-auto mt-1 mb-5">
                {{
                  userRole === 'admin'
                    ? 'Establish standardized annual leave categories (Casual, Sick, Maternity) for staff tracking.'
                    : 'Your school administration has not yet configured leave quotas for this academic session.'
                }}
              </p>
              <button
                v-if="userRole === 'admin'"
                @click="openPolicyModal"
                class="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl font-semibold text-xs sm:text-sm shadow-sm transition-colors cursor-pointer"
              >
                Create Leave Policy
              </button>
            </div>
          </div>

          <!-- CARD 3: TEACHER ATTENDANCE TIMING -->
          <div
            v-if="userRole === 'admin'"
            class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors"
          >
            <!-- Header -->
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Clock class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9]">
                    Teacher Attendance Timing
                  </h3>
                  <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8]">
                    Configure default morning check-in and afternoon departure cutoffs.
                  </p>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <!-- Check-In -->
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-[#A6B4C8] uppercase tracking-wider mb-2">
                  Check-In Cutoff Time
                </label>
                <div class="relative">
                  <input
                    v-model="attendanceTiming.checkInTime"
                    type="time"
                    class="w-full px-4 py-3 bg-white dark:bg-[#172235] text-gray-900 dark:text-[#F1F5F9] border border-gray-200 dark:border-[#263449] rounded-2xl focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:outline-none transition-colors text-base font-semibold shadow-xs"
                  />
                </div>
                <p class="text-[11px] text-gray-500 dark:text-[#7E8DA5] mt-1.5">
                  Teachers scanning after this time will be marked as "Late".
                </p>
              </div>

              <!-- Check-Out -->
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-[#A6B4C8] uppercase tracking-wider mb-2">
                  Check-Out Time
                </label>
                <div class="relative">
                  <input
                    v-model="attendanceTiming.checkOutTime"
                    type="time"
                    class="w-full px-4 py-3 bg-white dark:bg-[#172235] text-gray-900 dark:text-[#F1F5F9] border border-gray-200 dark:border-[#263449] rounded-2xl focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:outline-none transition-colors text-base font-semibold shadow-xs"
                  />
                </div>
                <p class="text-[11px] text-gray-500 dark:text-[#7E8DA5] mt-1.5">
                  Scheduled departure cutoff for regular daily school sessions.
                </p>
              </div>
            </div>

            <!-- Save Action Button -->
            <div class="mt-6 flex justify-end">
              <button
                @click="updateAttendanceTiming"
                :disabled="isSavingTiming"
                class="px-6 py-2.5 sm:py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl font-semibold text-xs sm:text-sm flex items-center gap-2 shadow-sm transition-colors disabled:opacity-60 cursor-pointer"
              >
                <svg
                  v-if="isSavingTiming"
                  class="animate-spin h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                    fill="none"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                <span>{{ isSavingTiming ? "Saving..." : "Update Timing" }}</span>
              </button>
            </div>
          </div>

          <!-- CARD 4: CAMPUS LOCATION & ADDRESS -->
          <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <MapPin class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9]">
                    Campus Location & Address
                  </h3>
                  <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8]">
                    Physical postal location and geographic jurisdiction.
                  </p>
                </div>
              </div>
            </div>

            <!-- Complete Address Banner -->
            <div class="bg-gray-50/80 dark:bg-[#18243A] rounded-2xl p-5 border border-gray-200/70 dark:border-[#263449] mb-5">
              <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div class="flex items-start gap-3">
                  <MapPin class="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#A6B4C8] mb-1">
                      Full Address
                    </h4>
                    <p class="text-base font-bold text-gray-900 dark:text-[#F1F5F9] leading-relaxed">
                      {{ schoolData.address }}
                    </p>
                    <p v-if="schoolData.village || schoolData.postOffice" class="text-sm text-gray-600 dark:text-[#A6B4C8] mt-1">
                      <span v-if="schoolData.village">Village: {{ schoolData.village }} · </span>
                      <span v-if="schoolData.postOffice">Post Office: {{ schoolData.postOffice }}</span>
                    </p>
                    <p class="text-sm text-gray-600 dark:text-[#A6B4C8]">
                      {{ schoolData.city }}, {{ schoolData.state }} - {{ schoolData.pincode }} ({{ getCountryName(schoolData.country) }})
                    </p>
                  </div>
                </div>

                <a
                  :href="getMapUrl()"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="px-4 py-2 bg-white dark:bg-[#111C30] hover:bg-gray-100 dark:hover:bg-[#1F2E4A] text-gray-800 dark:text-[#F1F5F9] border border-gray-200 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1.5 self-start flex-shrink-0 shadow-xs cursor-pointer"
                >
                  <Map class="w-3.5 h-3.5 text-emerald-500" />
                  <span>Google Maps</span>
                  <ExternalLink class="w-3 h-3 text-gray-400" />
                </a>
              </div>
            </div>

            <!-- Address Components Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
              <div class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  City
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.city }}
                </p>
              </div>

              <div class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  State
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.state }}
                </p>
              </div>

              <div class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  PIN Code
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.pincode }}
                </p>
              </div>

              <div v-if="schoolData.village" class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  Village
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.village }}
                </p>
              </div>

              <div v-if="schoolData.postOffice" class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  Post Office
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ schoolData.postOffice }}
                </p>
              </div>

              <div class="bg-gray-50/60 dark:bg-[#18243A]/70 rounded-xl p-3 border border-gray-100 dark:border-[#263449]/60">
                <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block mb-1">
                  Country
                </span>
                <p class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] truncate">
                  {{ getCountryName(schoolData.country) }}
                </p>
              </div>
            </div>
          </div>

          <!-- CARD 5: ABOUT SCHOOL / STORY (IF PROVIDED) -->
          <div
            v-if="schoolData.description || schoolData.aboutSchool"
            class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors"
          >
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/40 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <FileText class="w-5 h-5" />
              </div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9]">
                About the School
              </h3>
            </div>
            <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-5 border border-gray-200/60 dark:border-[#263449]">
              <p class="text-sm sm:text-base text-gray-700 dark:text-[#A6B4C8] leading-relaxed whitespace-pre-line">
                {{ schoolData.description || schoolData.aboutSchool }}
              </p>
            </div>
          </div>
        </div>

        <!-- ======================================= -->
        <!-- RIGHT COLUMN (1 COL)                    -->
        <!-- ======================================= -->
        <div class="space-y-6 sm:space-y-8">
          <!-- CARD 1: CONTACT CHANNELS -->
          <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-100 dark:border-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Phone class="w-5 h-5" />
              </div>
              <div>
                <h3 class="text-lg font-bold text-gray-900 dark:text-[#F1F5F9]">
                  Contact & Desk
                </h3>
                <p class="text-xs text-gray-500 dark:text-[#A6B4C8]">
                  Official communication lines.
                </p>
              </div>
            </div>

            <div class="space-y-3">
              <!-- Primary Phone -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 flex items-center justify-between gap-3 group transition-colors">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-950/60 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
                    <Phone class="w-4 h-4" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block">
                      Primary Phone
                    </span>
                    <a
                      :href="`tel:${schoolData.primaryPhone}`"
                      class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors truncate block"
                    >
                      {{ schoolData.primaryPhone || 'Not Configured' }}
                    </a>
                  </div>
                </div>
                <button
                  v-if="schoolData.primaryPhone"
                  @click="copyToClipboard(schoolData.primaryPhone, 'Primary Phone')"
                  class="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors cursor-pointer"
                  title="Copy Phone"
                >
                  <Check v-if="copiedField === 'Primary Phone'" class="w-4 h-4 text-emerald-500" />
                  <Copy v-else class="w-4 h-4" />
                </button>
              </div>

              <!-- Secondary Phone -->
              <div v-if="schoolData.secondaryPhone" class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 flex items-center justify-between gap-3 group transition-colors">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-950/60 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
                    <PhoneCall class="w-4 h-4" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block">
                      Secondary Phone
                    </span>
                    <a
                      :href="`tel:${schoolData.secondaryPhone}`"
                      class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                    >
                      {{ schoolData.secondaryPhone }}
                    </a>
                  </div>
                </div>
                <button
                  @click="copyToClipboard(schoolData.secondaryPhone, 'Secondary Phone')"
                  class="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors cursor-pointer"
                  title="Copy Phone"
                >
                  <Check v-if="copiedField === 'Secondary Phone'" class="w-4 h-4 text-emerald-500" />
                  <Copy v-else class="w-4 h-4" />
                </button>
              </div>

              <!-- Email -->
              <div class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 flex items-center justify-between gap-3 group transition-colors">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                    <Mail class="w-4 h-4" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block">
                      Email Address
                    </span>
                    <a
                      :href="`mailto:${schoolData.email}`"
                      class="text-sm font-bold text-gray-900 dark:text-[#F1F5F9] hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors truncate block"
                    >
                      {{ schoolData.email || 'Not Configured' }}
                    </a>
                  </div>
                </div>
                <button
                  v-if="schoolData.email"
                  @click="copyToClipboard(schoolData.email, 'Email Address')"
                  class="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors cursor-pointer"
                  title="Copy Email"
                >
                  <Check v-if="copiedField === 'Email Address'" class="w-4 h-4 text-emerald-500" />
                  <Copy v-else class="w-4 h-4" />
                </button>
              </div>

              <!-- Website -->
              <div v-if="schoolData.website" class="bg-gray-50/70 dark:bg-[#18243A] rounded-2xl p-4 border border-gray-100 dark:border-[#263449]/70 flex items-center justify-between gap-3 group transition-colors">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 rounded-xl bg-cyan-100 dark:bg-cyan-950/60 flex items-center justify-center text-cyan-600 dark:text-cyan-400 flex-shrink-0">
                    <Globe class="w-4 h-4" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-[#A6B4C8] block">
                      Website URL
                    </span>
                    <a
                      :href="schoolData.website"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:underline truncate block"
                    >
                      {{ schoolData.website }}
                    </a>
                  </div>
                </div>
                <a
                  :href="schoolData.website"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
                >
                  <ExternalLink class="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <!-- CARD 2: QUICK MANAGEMENT SHORTCUTS -->
          <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-gray-200/80 dark:border-[#263449] p-6 sm:p-8 shadow-sm transition-colors">
            <div class="flex items-center gap-3 mb-5">
              <div class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <Compass class="w-5 h-5" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-[#F1F5F9]">
                Quick Shortcuts
              </h3>
            </div>

            <div class="space-y-2">
              <router-link
                to="/teacher"
                class="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 hover:bg-indigo-50/50 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] border border-gray-100 dark:border-[#263449]/70 text-gray-900 dark:text-[#F1F5F9] transition-all group cursor-pointer"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-950/60 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <Users class="w-4 h-4" />
                  </div>
                  <span class="text-sm font-semibold">Teacher Directory</span>
                </div>
                <ChevronRight class="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </router-link>

              <router-link
                to="/students"
                class="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 hover:bg-emerald-50/50 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] border border-gray-100 dark:border-[#263449]/70 text-gray-900 dark:text-[#F1F5F9] transition-all group cursor-pointer"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    <UserCheck class="w-4 h-4" />
                  </div>
                  <span class="text-sm font-semibold">Student Roster</span>
                </div>
                <ChevronRight class="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </router-link>

              <router-link
                to="/Class"
                class="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 hover:bg-purple-50/50 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] border border-gray-100 dark:border-[#263449]/70 text-gray-900 dark:text-[#F1F5F9] transition-all group cursor-pointer"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-950/60 flex items-center justify-center text-purple-600 dark:text-purple-400">
                    <Layers class="w-4 h-4" />
                  </div>
                  <span class="text-sm font-semibold">Classes & Sections</span>
                </div>
                <ChevronRight class="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </router-link>

              <router-link
                to="/report"
                class="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 hover:bg-amber-50/50 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] border border-gray-100 dark:border-[#263449]/70 text-gray-900 dark:text-[#F1F5F9] transition-all group cursor-pointer"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-400">
                    <BarChart3 class="w-4 h-4" />
                  </div>
                  <span class="text-sm font-semibold">Reports & Analytics</span>
                </div>
                <ChevronRight class="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </router-link>

              <router-link
                v-if="userRole === 'admin'"
                to="/editSchool"
                class="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 hover:bg-indigo-50/50 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] border border-gray-100 dark:border-[#263449]/70 text-gray-900 dark:text-[#F1F5F9] transition-all group cursor-pointer"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                    <Settings class="w-4 h-4" />
                  </div>
                  <span class="text-sm font-semibold">Edit School Details</span>
                </div>
                <ChevronRight class="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================= -->
  <!-- PREMIUM SKELETON / LOADING STATE          -->
  <!-- ========================================= -->
  <div
    v-else-if="isLoading"
    class="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6"
  >
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Loading Header -->
      <div class="h-44 sm:h-52 rounded-3xl skeleton-shimmer border border-slate-200/80 dark:border-[#263449]"></div>

      <!-- Loading Grid -->
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div class="xl:col-span-2 space-y-6">
          <div class="h-64 rounded-3xl skeleton-shimmer border border-slate-200/80 dark:border-[#263449]"></div>
          <div class="h-64 rounded-3xl skeleton-shimmer border border-slate-200/80 dark:border-[#263449]"></div>
        </div>
        <div class="space-y-6">
          <div class="h-72 rounded-3xl skeleton-shimmer border border-slate-200/80 dark:border-[#263449]"></div>
          <div class="h-56 rounded-3xl skeleton-shimmer border border-slate-200/80 dark:border-[#263449]"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================= -->
  <!-- NO SCHOOL FOUND STATE                     -->
  <!-- ========================================= -->
  <div
    v-else
    class="min-h-[70vh] flex items-center justify-center px-4"
  >
    <div class="max-w-md w-full text-center bg-white dark:bg-[#111C30] border border-gray-200 dark:border-[#263449] rounded-3xl p-8 shadow-xl">
      <div class="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400">
        <School class="w-8 h-8" />
      </div>
      <h3 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9] mb-2">
        No School Profile Found
      </h3>
      <p class="text-sm text-gray-500 dark:text-[#A6B4C8] mb-6 leading-relaxed">
        We couldn't retrieve the institutional details for this account. Create a new school profile or re-authenticate.
      </p>
      <div class="space-y-3">
        <button
          @click="reloadPage"
          class="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm cursor-pointer"
        >
          Reload Page
        </button>
        <router-link
          to="/createSchool"
          class="w-full py-3 bg-gray-100 hover:bg-gray-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] text-gray-900 dark:text-[#F1F5F9] rounded-xl font-semibold text-sm transition-colors block text-center cursor-pointer"
        >
          Create New School
        </router-link>
      </div>
    </div>
  </div>

  <!-- ========================================= -->
  <!-- MODAL: CREATE LEAVE POLICY                -->
  <!-- ========================================= -->
  <transition name="modal">
    <div
      v-if="showPolicyForm"
      class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white dark:bg-[#111C30] border border-gray-200 dark:border-[#263449] rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl transition-colors">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-[#F1F5F9]">
              Configure Leave Policy
            </h2>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-[#A6B4C8] mt-1">
              Define standard categories and annual day quotas for school staff.
            </p>
          </div>
          <button
            @click="showPolicyForm = false"
            class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors cursor-pointer"
          >
            ✕
          </button>
        </div>

        <!-- Leave Types Form -->
        <div class="space-y-3 mb-6 max-h-[50vh] overflow-y-auto pr-1">
          <div
            v-for="(leaveType, index) in policyForm.leaveTypes"
            :key="index"
            class="flex flex-col sm:flex-row gap-3 sm:items-end p-3 rounded-2xl bg-gray-50 dark:bg-[#18243A] border border-gray-200/60 dark:border-[#263449]"
          >
            <div class="flex-1">
              <label class="block text-xs font-semibold text-gray-600 dark:text-[#A6B4C8] uppercase tracking-wider mb-1">
                Leave Type Name
              </label>
              <input
                v-model="leaveType.type"
                type="text"
                placeholder="e.g. Casual, Sick"
                class="w-full px-3.5 py-2.5 bg-white dark:bg-[#172235] text-gray-900 dark:text-[#F1F5F9] border border-gray-300 dark:border-[#263449] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              />
            </div>

            <div class="sm:w-36">
              <label class="block text-xs font-semibold text-gray-600 dark:text-[#A6B4C8] uppercase tracking-wider mb-1">
                Yearly Limit
              </label>
              <input
                v-model.number="leaveType.yearlyLimit"
                type="number"
                min="0"
                placeholder="12"
                class="w-full px-3.5 py-2.5 bg-white dark:bg-[#172235] text-gray-900 dark:text-[#F1F5F9] border border-gray-300 dark:border-[#263449] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              />
            </div>

            <button
              @click="removeLeaveType(index)"
              class="px-3.5 py-2.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 rounded-xl text-xs font-semibold transition-colors cursor-pointer self-end sm:self-auto"
            >
              Remove
            </button>
          </div>
        </div>

        <!-- Add More Button -->
        <button
          @click="addLeaveType"
          class="mb-6 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] text-gray-800 dark:text-[#F1F5F9] rounded-xl text-xs font-semibold transition-colors cursor-pointer"
        >
          + Add Leave Type
        </button>

        <!-- Form Actions -->
        <div class="flex gap-3 justify-end pt-4 border-t border-gray-200/80 dark:border-[#263449]">
          <button
            @click="showPolicyForm = false"
            class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] text-gray-700 dark:text-[#F1F5F9] rounded-xl text-sm font-semibold transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            @click="submitPolicyForm"
            class="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm cursor-pointer"
          >
            Create Policy
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- ========================================= -->
  <!-- MODAL: EDIT TOTAL LEAVE                   -->
  <!-- ========================================= -->
  <div
    v-if="showEditModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
  >
    <div class="w-full max-w-md bg-white dark:bg-[#111C30] border border-gray-200 dark:border-[#263449] rounded-3xl shadow-2xl p-6 sm:p-8 relative transition-colors">
      <!-- Close Button -->
      <button
        @click="showEditModal = false"
        class="absolute top-5 right-5 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors cursor-pointer"
      >
        ✕
      </button>

      <!-- Title -->
      <h2 class="text-xl font-bold text-gray-900 dark:text-[#F1F5F9] mb-1 flex items-center gap-2">
        <span class="p-2 rounded-xl bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
          <Pencil class="w-4 h-4" />
        </span>
        Update Leave Allocation
      </h2>
      <p class="text-xs text-gray-500 dark:text-[#A6B4C8] mb-6">
        Adjusting quota for <strong class="text-indigo-600 dark:text-indigo-400">{{ editingBalance.type }}</strong>
      </p>

      <!-- Input Section -->
      <div class="mb-6">
        <label class="block text-xs font-semibold text-gray-600 dark:text-[#A6B4C8] uppercase tracking-wider mb-2">
          New Total Days
        </label>
        <input
          v-model.number="editingBalance.newTotal"
          type="number"
          min="0"
          class="w-full px-4 py-3 bg-white dark:bg-[#172235] text-gray-900 dark:text-[#F1F5F9] border border-gray-200 dark:border-[#263449] rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg font-bold transition-colors"
        />
        <div class="mt-2 text-xs text-gray-500 dark:text-[#A6B4C8] flex justify-between">
          <span>Current Allocation: {{ editingBalance.total }} days</span>
          <span>Used: {{ editingBalance.used }} days</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          @click="showEditModal = false"
          class="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E4A] text-gray-700 dark:text-[#F1F5F9] font-semibold text-sm transition-colors cursor-pointer"
        >
          Cancel
        </button>

        <button
          @click="submitEditBalance"
          :disabled="isUpdating"
          class="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-60 cursor-pointer"
        >
          <svg
            v-if="isUpdating"
            class="animate-spin h-4 w-4 text-white"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <span>{{ isUpdating ? "Updating..." : "Confirm Update" }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Reusable Confirm Modal -->
  <ConfirmModal
    :show="showConfirmModal"
    :title="confirmConfig.title"
    :description="confirmConfig.description"
    :buttonText="confirmConfig.buttonText"
    :isDestructive="confirmConfig.isDestructive"
    :isLoading="confirmLoading"
    @cancel="showConfirmModal = false"
    @confirm="confirmConfig.action"
  />
</template>

<script setup>
  import { ref, onMounted, computed, reactive } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  import ConfirmModal from "@/components/Students/ConfirmationPopupModal.vue";
  import {
    Edit,
    School,
    GraduationCap,
    Calendar,
    MapPin,
    Info,
    Map,
    Mail,
    Globe,
    Phone,
    PhoneCall,
    FileText,
    Users,
    UserCheck,
    BarChart3,
    Settings,
    Clock,
    MoreVertical,
    Edit3,
    Trash2,
    Pencil,
    CalendarDays,
    ExternalLink,
    ShieldCheck,
    ChevronRight,
    Copy,
    Check,
    Compass,
    Award,
    Layers,
  } from "lucide-vue-next";
  import store from "@/store";

  const { showToast } = useGlobalToast();

  // 🎯 UI state
  const showMenu = ref(false);
  const isLoading = ref(true);
  const showPolicyForm = ref(false);
  const showEditModal = ref(false);
  const isUpdating = ref(false);
  const showConfirmModal = ref(false);
  const confirmLoading = ref(false);
  const copiedField = ref("");

  const attendanceTiming = reactive({
    checkInTime: "09:00",
    checkOutTime: "14:30",
  });

  const isSavingTiming = ref(false);
  const route = useRoute();
  const router = useRouter();

  const confirmConfig = reactive({
    title: "",
    description: "",
    buttonText: "Confirm",
    isDestructive: false,
    action: null,
  });

  const activeMenu = ref(null);

  const toggleMenu = (index) => {
    activeMenu.value = activeMenu.value === index ? null : index;
  };

  const closeMenu = () => {
    activeMenu.value = null;
  };

  // ✅ Default Leave Types
  const DEFAULT_LEAVE_TYPES = [
    { type: "Casual", yearlyLimit: 12 },
    { type: "Sick", yearlyLimit: 10 },
    { type: "Personal", yearlyLimit: 5 },
    { type: "Maternity", yearlyLimit: 90 },
    { type: "Paternity", yearlyLimit: 15 },
    { type: "Unpaid", yearlyLimit: 0 },
  ];

  // ✅ Policy form state
  const policyForm = reactive({
    leaveTypes: [...DEFAULT_LEAVE_TYPES],
  });

  const openPolicyModal = () => {
    policyForm.leaveTypes = [...DEFAULT_LEAVE_TYPES];
    showPolicyForm.value = true;
  };

  // Edit balance state
  const editingBalance = reactive({
    type: "",
    total: 0,
    used: 0,
    remaining: 0,
    newTotal: 0,
  });

  const openEditModal = (item) => {
    editingBalance.type = item.type;
    editingBalance.total = item.total;
    editingBalance.used = item.used;
    editingBalance.remaining = item.remaining;
    editingBalance.newTotal = item.total;
    showEditModal.value = true;
  };

  const submitEditBalance = async () => {
    if (editingBalance.newTotal < editingBalance.used) {
      showToast("error", "New total cannot be less than used leaves!");
      return;
    }

    try {
      isUpdating.value = true;
      await store.dispatch("updateLeaveBalance", {
        leaveType: editingBalance.type,
        total: editingBalance.newTotal,
      });

      await store.dispatch("fetchMyLeaveBalance");
      showEditModal.value = false;
      showToast("success", "Leave balance updated successfully!");
    } catch (err) {
      console.error("Failed to update balance", err);
      showToast("error", "Failed to update leave balance");
    } finally {
      isUpdating.value = false;
    }
  };

  const formatTimeForInput = (val) => {
    if (!val) return "";
    if (typeof val === "string") {
      let clean = val.trim();
      if (clean.includes("T")) {
        clean = clean.split("T")[1];
      }
      return clean.slice(0, 5);
    }
    if (val instanceof Date && !isNaN(val)) {
      return val.toTimeString().slice(0, 5);
    }
    return "";
  };

  const updateAttendanceTiming = async () => {
    try {
      isSavingTiming.value = true;

      const cleanCheckIn = formatTimeForInput(attendanceTiming.checkInTime);
      const cleanCheckOut = formatTimeForInput(attendanceTiming.checkOutTime);

      if (!cleanCheckIn || !cleanCheckOut) {
        showToast("error", "Please provide both check-in and check-out time");
        return;
      }

      await store.dispatch("updateTeacherAttendanceTiming", {
        checkInTime: cleanCheckIn,
        checkOutTime: cleanCheckOut,
      });

      showToast("success", "Teacher attendance timing updated!");
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update attendance timing";
      showToast("error", errMsg);
    } finally {
      isSavingTiming.value = false;
    }
  };

  const requestDeleteLeaveType = (item) => {
    if (item.used > 0) {
      showToast(
        "error",
        `Cannot delete ${item.type} because ${item.used} leave(s) have already been used.`,
      );
      return;
    }

    confirmConfig.title = "Delete Leave Type";
    confirmConfig.description = `Are you sure you want to permanently delete "${item.type}"? This action cannot be undone.`;
    confirmConfig.buttonText = "Delete";
    confirmConfig.isDestructive = true;

    confirmConfig.action = async () => {
      confirmLoading.value = true;

      try {
        await store.dispatch("deleteLeaveType", item.type);
        await store.dispatch("fetchMyLeaveBalance");

        showToast("success", "Leave type deleted successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to delete leave type.",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  const addLeaveType = () => {
    policyForm.leaveTypes.push({ type: "", yearlyLimit: 0 });
  };

  const removeLeaveType = (index) => {
    policyForm.leaveTypes.splice(index, 1);
  };

  const submitPolicyForm = async () => {
    if (policyForm.leaveTypes.length === 0) {
      showToast("error", "Please add at least one leave type");
      return;
    }

    const isValid = policyForm.leaveTypes.every(
      (lt) =>
        lt.type &&
        lt.type.trim().length > 0 &&
        lt.yearlyLimit !== null &&
        lt.yearlyLimit !== undefined &&
        lt.yearlyLimit >= 0,
    );

    if (!isValid) {
      showToast("error", "Please fill in all fields correctly");
      return;
    }

    confirmConfig.title = "Create Leave Policy";
    confirmConfig.description =
      "Are you sure you want to establish this leave policy? This will apply to all staff across the institution.";
    confirmConfig.buttonText = "Establish Policy";
    confirmConfig.isDestructive = false;

    confirmConfig.action = async () => {
      confirmLoading.value = true;

      try {
        await store.dispatch("createOrUpdateLeavePolicy", {
          leaveTypes: policyForm.leaveTypes,
        });

        await store.dispatch("fetchMyLeaveBalance");

        showPolicyForm.value = false;
        policyForm.leaveTypes = [...DEFAULT_LEAVE_TYPES];

        showToast("success", "Leave policy created successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to create leave policy",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  // 🌍 Country lookup
  const countries = ref([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" },
    { code: "UK", name: "United Kingdom" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "JP", name: "Japan" },
    { code: "CN", name: "China" },
    { code: "BR", name: "Brazil" },
  ]);

  // 🔁 Fetch school details on mount
  onMounted(async () => {
    try {
      isLoading.value = true;

      await store.dispatch("fetchMySchool");
      await store.dispatch("fetchMyLeaveBalance");

      try {
        const timing = await store.dispatch("fetchTeacherAttendanceTiming");
        if (timing) {
          if (timing.checkInTime) {
            attendanceTiming.checkInTime =
              formatTimeForInput(timing.checkInTime) || "09:00";
          }
          if (timing.checkOutTime) {
            attendanceTiming.checkOutTime =
              formatTimeForInput(timing.checkOutTime) || "15:00";
          }
        }
      } catch (err) {
        console.error(
          "❌ Failed to fetch attendance timing:",
          err.response?.data || err,
        );
      }

      if (route.query.openPolicy === "true") {
        openPolicyModal();
        router.replace({ query: {} });
      }
    } catch (err) {
      console.error("❌ Failed to load school data:", err);
      showToast("error", "Failed to load school dashboard");
    } finally {
      isLoading.value = false;
    }
  });

  const schoolData = computed(() => store.state.schools[0] || null);
  const userRole = computed(() => store.state.userRole);
  const leaveBalance = computed(() => store.state.leaveBalance);

  const getSchoolAge = () => {
    if (!schoolData.value?.establishmentYear) return "N/A";
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(schoolData.value.establishmentYear);
    return isNaN(age) || age < 0 ? 0 : age;
  };

  const getCountryName = (code) => {
    const country = countries.value.find((c) => c.code === code);
    return country ? country.name : (code || "India");
  };

  const getMapUrl = () => {
    if (schoolData.value?.googleMapLink) return schoolData.value.googleMapLink;
    const parts = [
      schoolData.value?.schoolName,
      schoolData.value?.address,
      schoolData.value?.city,
      schoolData.value?.state,
      schoolData.value?.pincode,
    ].filter(Boolean);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(", "))}`;
  };

  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    copiedField.value = field;
    showToast("success", `${field} copied to clipboard!`);
    setTimeout(() => {
      copiedField.value = "";
    }, 2000);
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest(".relative")) {
      showMenu.value = false;
      activeMenu.value = null;
    }
  };

  if (typeof window !== "undefined") {
    document.addEventListener("click", handleClickOutside);
  }

  const reloadPage = () => {
    window.location.reload();
  };
</script>

<style scoped>
  /* Modal Smooth Animation */
  .modal-enter-active {
    transition: all 260ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .modal-leave-active {
    transition: all 200ms ease;
  }
  .modal-enter-from {
    opacity: 0;
    transform: translateY(20px) scale(0.97);
  }
  .modal-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  .modal-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  .modal-leave-to {
    opacity: 0;
    transform: translateY(15px) scale(0.97);
  }
</style>
